
import { defineComponent } from 'vue'
import { createValidations, ICustomValidatorMap } from '@/helpers/commons/validationHelper'
import { AxiosResponse } from 'axios'
import useValidate from '@vuelidate/core'
import detailTabDescriptor from './DatasetManagementDetailCardDescriptor.json'
import KnValidationMessages from '@/components/UI/KnValidatonMessages.vue'
import Card from 'primevue/card'
import Dropdown from 'primevue/dropdown'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import AutoComplete from 'primevue/autocomplete'
import { formatDateWithLocale } from '@/helpers/commons/localeHelper'

export default defineComponent({
    components: { Card, Dropdown, KnValidationMessages, DataTable, Column, AutoComplete },
    props: {
        scopeTypes: { type: Array as any, required: true },
        categoryTypes: { type: Array as any, required: true },
        selectedDataset: { type: Object as any },
        selectedDatasetVersions: { type: Array as any },
        availableTags: { type: Array as any },
        loading: { type: Boolean }
    },
    computed: {
        noDatasetVersions(): any {
            if (this.selectedDatasetVersions.length > 0) {
                return false
            }
            return true
        }
    },
    emits: ['touched', 'reloadVersions', 'loadingOlderVersion', 'olderVersionLoaded'],
    data() {
        return {
            detailTabDescriptor,
            loadingVersion: false,
            v$: useValidate() as any,
            dataset: {} as any,
            datasetVersions: [] as any,
            availableTagsNames: [] as any,
            selectedTagsNames: [] as any,
            filteredTagsNames: null as any
        }
    },
    created() {
        this.dataset = this.selectedDataset
    },
    watch: {
        selectedDataset() {
            this.dataset = this.selectedDataset
            this.v$.dataset.label.$touch()
            this.v$.dataset.name.$touch()
        }
    },
    validations() {
        const catTypeRequired = (value) => {
            return this.dataset.scopeCd == 'USER' || value
        }
        const customValidators: ICustomValidatorMap = { 'cat-type-required': catTypeRequired }
        const validationObject = { dataset: createValidations('dataset', detailTabDescriptor.validations.dataset, customValidators) }
        return validationObject
    },
    methods: {
        //#region ===================== Delete Versions Functionality ====================================================
        deleteConfirm(deletetype, event) {
            console.log(event)
            let msgDesc = ''
            deletetype === 'deleteOne' ? (msgDesc = 'managers.datasetManagement.deleteOneVersionMsg') : (msgDesc = 'managers.datasetManagement.deleteAllVersionsMsg')
            this.$confirm.require({
                message: this.$t(msgDesc),
                header: this.$t('common.uppercaseDelete'),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    deletetype === 'deleteOne' ? this.deleteSelectedVersion(event) : this.deleteAllVersions()
                }
            })
        },
        async deleteSelectedVersion(event) {
            return this.$http
                .delete(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/${event.dsId}/version/${event.versNum}`)
                .then(() => {
                    this.$store.commit('setInfo', { title: this.$t('common.toast.deleteTitle'), msg: this.$t('common.toast.deleteSuccess') })
                    this.$emit('reloadVersions')
                })
                .catch((error) => this.$store.commit('setError', { title: this.$t('common.error.generic'), msg: error.message }))
        },
        async deleteAllVersions() {
            return this.$http
                .delete(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/${this.selectedDataset.id}/allversions/`)
                .then(() => {
                    this.$store.commit('setInfo', { title: this.$t('common.toast.deleteTitle'), msg: this.$t('managers.datasetManagement.deleteAllVersionsSuccess') })
                    this.$emit('reloadVersions')
                })
                .catch((error) => this.$store.commit('setError', { title: this.$t('common.error.generic'), msg: error.message }))
        },
        //#endregion ================================================================================================

        //#region ===================== Restore Versions Functionality ====================================================
        restoreVersionConfirm(event) {
            this.$confirm.require({
                icon: 'pi pi-exclamation-triangle',
                message: this.$t('managers.datasetManagement.restoreMsg'),
                header: this.$t('managers.datasetManagement.restoreTitle'),
                accept: () => this.restoreVersion(event)
            })
        },
        async restoreVersion(dsToRestore) {
            this.$emit('loadingOlderVersion')
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/${this.dataset.id}/restore?versionId=${dsToRestore.versNum}`).then((response: AxiosResponse<any>) => {
                this.dataset.dsTypeCd.toLowerCase() == 'file' ? this.refactorFileDatasetConfig(response.data[0]) : ''
                this.$emit('olderVersionLoaded', response.data[0])
            })
            this.loadingVersion = false
        },
        refactorFileDatasetConfig(item) {
            this.dataset.fileType = item != undefined ? item.fileType : ''
            this.dataset.fileName = item != undefined ? item.fileName : ''
            this.dataset.csvEncoding = item != undefined ? item.csvEncoding : 'UTF-8'
            this.dataset.csvDelimiter = item != undefined ? item.csvDelimiter : ','
            this.dataset.csvQuote = item != undefined ? item.csvQuote : '"'
            this.dataset.dateFormat = item != undefined && item.dateFormat != undefined ? item.dateFormat : 'dd/MM/yyyy'
            this.dataset.timestampFormat = item != undefined && item.timestampFormat != undefined ? item.timestampFormat : 'dd/MM/yyyy HH:mm:ss'

            if (item != undefined) {
                if (item.limitRows != null && item.limitRows != '') {
                    this.dataset.limitRows = Number(item.limitRows)
                } else {
                    this.dataset.limitRows = item.limitRows
                }
            } else {
                this.dataset.limitRows = null
            }
            
            if (item != undefined) {
                this.dataset.catTypeVn = item.catTypeVn
                this.dataset.catTypeId = Number(item.catTypeId)
                this.dataset.xslSheetNumber = Number(1)
                this.dataset.skipRows = Number(item.skipRows)
                this.dataset.limitRows = Number(null)
            } else {
                this.dataset.catTypeVn = ''
                this.dataset.catTypeId = null
                this.dataset.xslSheetNumber = null
                this.dataset.skipRows = null
                this.dataset.limitRows = null
            }

            this.dataset.id = item != undefined ? item.id : ''
            this.dataset.label = item != undefined ? item.label : ''
            this.dataset.name = item != undefined ? item.name : ''
            this.dataset.description = item != undefined ? item.description : ''
            this.dataset.meta = item != undefined ? item.meta : []

            this.dataset.fileUploaded = false
        },
        //#endregion ================================================================================================

        //#region ===================== Tags Functionality ====================================================
        searchTag(event) {
            setTimeout(() => {
                if (!event.query.trim().length) {
                    this.filteredTagsNames = [...this.availableTags]
                } else {
                    this.filteredTagsNames = this.availableTags.filter((tag) => {
                        return tag.name.toLowerCase().startsWith(event.query.toLowerCase())
                    })
                }
            }, 250)
        },
        createTagChip(event: any) {
            if (event.target.value) {
                const tempWord = this.availableTags.find((el) => el.name == event.target.value)
                if (!tempWord) {
                    this.dataset.tags.push(event.target.value)
                    this.buildTagObject()
                    event.target.value = ''
                }
            }
        },
        buildTagObject() {
            this.dataset.tags = this.dataset.tags.map((tag) => {
                if (typeof tag !== 'string') {
                    return tag
                } else {
                    return { name: tag }
                }
            })
        },
        //#endregion ================================================================================================

        formatDate(date) {
            return formatDateWithLocale(date, { dateStyle: 'short', timeStyle: 'short' })
        },
        updateCdFromId(optionsArray, fieldToUpdate, updatedField) {
            const selectedField = optionsArray.find((option) => option.VALUE_ID === updatedField)
            if(selectedField) this.dataset[fieldToUpdate] = selectedField.VALUE_CD
        }
    }
})
